import type { UseByRecipeListContentBlock } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import type { RecipePendingStates } from '../components/BasketModal/Content/RecipesGrouped'

type RecipeSelection = {
  id: RecipeUUID
  quantity: number
  numPortions: number
}

type RecipeSelectionSummary = {
  quantity: number
  portionCounts: Set<number>
}

const summariseSelections = (selections: RecipeSelection[]) =>
  selections.reduce<Map<RecipeUUID, RecipeSelectionSummary>>((summaries, selection) => {
    const summary = summaries.get(selection.id) ?? {
      quantity: 0,
      portionCounts: new Set<number>(),
    }

    summary.quantity += selection.quantity
    summary.portionCounts.add(selection.numPortions)
    summaries.set(selection.id, summary)

    return summaries
  }, new Map())

const setsAreEqual = <T>(first: Set<T>, second: Set<T>) =>
  first.size === second.size && Array.from(first).every((value) => second.has(value))

export const getGroupedRecipePendingStates = (
  localRecipes: RecipeSelection[],
  contentBlocks: UseByRecipeListContentBlock[],
): RecipePendingStates => {
  const localSummaries = summariseSelections(localRecipes)
  const contentSummaries = summariseSelections(
    contentBlocks.flatMap(({ groups }) => groups.flatMap(({ items }) => items)),
  )

  return Array.from(contentSummaries.entries()).reduce<RecipePendingStates>(
    (pendingStates, [recipeId, contentSummary]) => {
      const localSummary = localSummaries.get(recipeId)

      if (
        localSummary &&
        (localSummary.quantity !== contentSummary.quantity ||
          !setsAreEqual(localSummary.portionCounts, contentSummary.portionCounts))
      ) {
        pendingStates[recipeId] = 'updating-portions'
      }

      return pendingStates
    },
    {},
  )
}
